import {
  useCallback,
  useEffect,
  useRef,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { useSetAtom } from 'jotai'
import { twMerge } from 'tailwind-merge'
import * as Sentry from '@sentry/electron/renderer'
import { Archive } from 'lucide-react'
import { ActionIconSlot } from '@renderer/components/action-icon-slot'
import { getActionIcon } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { AskUserBadge } from '@renderer/chat/integrations/ask-user-card'
import { getReportOriginSkill, OriginSkillIcon } from '@renderer/chat/integrations/report-to-user'
import { useStagedActionEditOptions } from '../../../hooks/use-staged-actions'
import { ActionHeader } from '../../../components/action-header'
import { ActionSource } from '../../../components/action-source'
import { ConfidenceBar } from '../../../components/confidence-bar'
import { isUndrafted } from '../../meeting-view/action-items/send-readiness'
import { clearDraftTitle, splitEditableTitle, stageDraftTitle } from '../inline-drafts'
import { actionInlineDraftsAtom } from '../inline-draft-state'
import { useActionInlineDraftController } from '../use-action-inline-draft-controller'
import { focusWithCaretAtEnd } from '../caret'
import type { ActionViewModel } from '../use-action-view-model'

interface ActionViewHeaderProps {
  model: ActionViewModel
  showSource?: boolean
  /** Output cards enable title editing only after the user chooses Edit. */
  allowEditing?: boolean
  subIcon?: ReactNode
  /** Compact archived state shown beside the title on embedded output rows. */
  isArchived?: boolean
  /** Lets a synthetic output row promote one item from a compound action. */
  titleOverride?: string
  annotation?: ReactNode
  className?: string
}

/**
 * Title block: integration icon chip + action title, with the source
 * attribution ("<source> · <time ago>") as the secondary line. Either flavour of
 * ask-user action swaps the chip for the badge of the card it opens — amber for
 * a question, blue for feedback. While the draft is still the user's
 * (`isEditable`) the title's core is click-to-type editable (HIG-5830) — the
 * edit saves itself once the field is done, so the footer's Save is only ever
 * about the body.
 */
export function ActionViewHeader({
  model,
  showSource = true,
  allowEditing = true,
  subIcon,
  isArchived,
  titleOverride,
  annotation,
  className,
}: ActionViewHeaderProps) {
  const { action, state, editState, draft, isEditable } = model
  const { editOptions } = useStagedActionEditOptions({ actionId: isEditable ? action?.id : undefined })
  const titleCoreRef = useRef<HTMLSpanElement>(null)

  // The editable core only spans its own text — let a click anywhere in the
  // title block (padding, prefix, the wrap gap) start editing, caret at the
  // end. Clicks landing inside the span keep their native caret placement.
  const handleTitleAreaClick = useCallback((event: MouseEvent<HTMLHeadingElement>) => {
    const el = titleCoreRef.current
    if (!el || el.contains(event.target as Node) || document.activeElement === el) return
    focusWithCaretAtEnd(el)
  }, [])

  if (!action) {
    return null
  }

  // `||` on purpose: an empty-string title is unusable and should fall through.
  const title = titleOverride || action.view?.title || action.title || (state === 'question' ? 'Question' : 'Action')
  const coreTitle = editOptions?.title?.coreTitle
  const titleParts = allowEditing && isEditable && coreTitle ? splitEditableTitle(title, coreTitle) : null
  // A report attributed to a skill is badged with that skill's icon rather than
  // the integration glyph — the skill is what produced the message.
  const reportSkill = getReportOriginSkill(action)

  return (
    <ActionHeader
      className={className}
      icon={
        state === 'question' || state === 'feedback' ? (
          <AskUserBadge variant={state === 'feedback' ? 'feedback' : 'input'} />
        ) : (
          <ActionIconSlot view={action.view} subIcon={subIcon}>
            {reportSkill ? <OriginSkillIcon skillName={reportSkill} /> : getActionIcon(action)}
          </ActionIconSlot>
        )
      }
      title={
        <span
          data-staged-action-edit-box={titleParts ? '' : undefined}
          onClick={titleParts ? handleTitleAreaClick : undefined}
          className={twMerge('select-text', titleParts && 'cursor-text', isArchived && 'text-foreground-secondary')}
        >
          {titleParts ? (
            <>
              {titleParts.prefix}
              <EditableTitleCore
                coreRef={titleCoreRef}
                actionId={action.id}
                core={titleParts.core}
                draftTitle={draft?.title}
                baseContent={action.view?.content}
                // Blocked (but kept mounted, so a dirty title doesn't visually
                // revert) while a scoped-chat edit regenerates the draft.
                editable={state !== 'updating'}
              />
              {titleParts.suffix}
            </>
          ) : (
            title
          )}
          {isArchived && (
            <Archive aria-label="Archived" className="relative -top-px ml-1.5 inline size-3.5 align-middle" />
          )}
        </span>
      }
      subtitle={
        showSource ? (
          <>
            <ActionSource action={action} editConversationId={editState?.conversationId ?? null} />
            {/* The notch's confidence stays checkable here until the action is drafted */}
            {isUndrafted(action) && <ConfidenceBar action={action} className="text-foreground-secondary h-4 pb-px" />}
          </>
        ) : null
      }
      annotation={
        !showSource && annotation ? (
          <>
            <div className="mt-1 flex min-w-0 items-center">{annotation}</div>
            <hr className="-mx-3 mb-2 mt-3.5 border-t" />
          </>
        ) : null
      }
    />
  )
}

interface EditableTitleCoreProps {
  /** Owned by the header so the whole title block can delegate clicks here. */
  coreRef: RefObject<HTMLSpanElement>
  actionId: string
  core: string
  draftTitle: string | undefined
  /** Server body content, snapshotted into the draft on first touch for regeneration detection. */
  baseContent: string | undefined
  editable: boolean
}

/**
 * The editable region of the title. Uncontrolled contentEditable: React never
 * renders its text — the effect seeds it (draft first, so unsent edits survive
 * back-navigation) and re-syncs external changes only while unfocused, so a
 * concurrent regeneration can't clobber an in-progress edit.
 *
 * The field commits its own edit: a title is one value the user is done with the
 * moment they leave it, not a document to be saved.
 */
function EditableTitleCore({ coreRef, actionId, core, draftTitle, baseContent, editable }: EditableTitleCoreProps) {
  const setDrafts = useSetAtom(actionInlineDraftsAtom)
  const { commitTitle } = useActionInlineDraftController({
    actionId,
    coreTitle: core,
    serverContent: baseContent,
  })
  const reportUnexpectedCommitError = useCallback(
    (error: unknown) => {
      Sentry.captureException(error, {
        tags: { area: 'action-inline-draft', operation: 'commit-title' },
        extra: { actionId },
      })
    },
    [actionId],
  )

  useEffect(() => {
    const el = coreRef.current
    if (!el || document.activeElement === el) return
    const target = draftTitle ?? core
    if (el.textContent !== target) {
      el.textContent = target
    }
  }, [core, draftTitle])

  // Also commit on unmount, for the exits that never blur the field: hiding the
  // sidepanel by hotkey, or navigating into a nested view. Otherwise the edit
  // sits in a draft that nothing surfaces, since the footer belongs to the body.
  const commitRef = useRef(commitTitle)
  useEffect(() => {
    commitRef.current = commitTitle
  }, [commitTitle])
  useEffect(() => {
    return () => {
      void commitRef.current().catch(reportUnexpectedCommitError)
    }
  }, [reportUnexpectedCommitError])

  const handleInput = useCallback(
    (event: FormEvent<HTMLSpanElement>) => {
      const value = event.currentTarget.textContent ?? ''
      setDrafts((drafts) => stageDraftTitle(drafts, actionId, value, { coreTitle: core, content: baseContent }))
    },
    [actionId, core, baseContent, setDrafts],
  )

  // Leaving the field saves it. Errors are handled inside the commit (a failure
  // hands the title to the Save footer to retry), so nothing to catch here.
  const handleBlur = useCallback(() => {
    void commitTitle().catch(reportUnexpectedCommitError)
  }, [commitTitle, reportUnexpectedCommitError])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLSpanElement>) => {
      // Titles are single-line; Enter is done with the field, so blur saves it.
      if (event.key === 'Enter') {
        event.preventDefault()
        event.currentTarget.blur()
      }
      // Escape reverts the field. Drop the draft *before* blurring — the blur
      // that follows would otherwise commit the abandoned text — and the
      // unfocused-only sync effect then repaints the pristine core.
      if (event.key === 'Escape') {
        event.preventDefault()
        setDrafts((drafts) => clearDraftTitle(drafts, actionId))
        event.currentTarget.blur()
      }
    },
    [actionId, setDrafts],
  )

  const handlePaste = useCallback((event: ClipboardEvent<HTMLSpanElement>) => {
    event.preventDefault()
    const text = event.clipboardData.getData('text/plain').replace(/\s+/g, ' ')
    document.execCommand('insertText', false, text)
  }, [])

  return (
    <span
      ref={coreRef}
      role="textbox"
      aria-label="Action title"
      contentEditable={editable}
      tabIndex={editable ? 0 : -1}
      suppressContentEditableWarning
      spellCheck={false}
      // Chromeless like the body editor — the caret is the focus indicator.
      // (contentEditable always matches :focus-visible in Chromium, so a ring
      // here would show on mouse click too, not just keyboard focus.)
      className="cursor-text select-text outline-none empty:inline-block empty:min-w-1"
      onInput={handleInput}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
    />
  )
}
